// AI 프롬프트 관리 화면 3탭이 공유하는 라벨·배지 매핑과 응답 처리 유틸.
// 판정 상태 용어는 보완검토 화면(SupplementReview.vue)과 동일하게 맞춘다.

export const ASSET_TYPES = ['template', 'precedents', 'catalog'];

export const ASSET_TYPE_LABELS = {
    template: '판정 템플릿',
    precedents: '판례',
    catalog: '규칙 카탈로그',
};

// 반영 미리보기(apply-preview)에서 "이 피드백이 어디에 반영되나"를 평가자에게
// 보여줄 때 쓰는 동작형 라벨. ASSET_TYPE_LABELS(버전 이력용 명사)와 목적이 달라
// 별도로 둔다 — 기존 라벨은 그대로 유지.
export const PREVIEW_TARGET_LABELS = {
    template: '판정 규칙 개정',
    precedents: '판례 추가',
};

export function previewTargetLabel(type) {
    return PREVIEW_TARGET_LABELS[type] || ASSET_TYPE_LABELS[type] || type || '-';
}

// 자산 종류 선택 옵션.
// ⛔ 영문 키(precedents 등)를 라벨에 노출하지 않는다 — 이 화면을 쓰는 사람은 ESG
// 평가자이지 프롬프트 자산 구조를 아는 사람이 아니다. 무엇을 고르는지 설명이
// 필요한 선택지는 애초에 기본 화면에 둘 것이 아니라서, 이 옵션은 고급 설정에서만
// 쓰인다(기본값 precedents — 평가자 피드백은 거의 전부 판례로 쌓이는 것이 정상 경로).
export const ASSET_TYPE_OPTIONS = ASSET_TYPES.map((v) => ({
    value: v,
    text: ASSET_TYPE_LABELS[v],
}));

export const SOURCE_TYPE_LABELS = {
    override: '판정 뒤집기',
    note: '내부 노트',
    feedback_edit: '발송 문구 수정',
    manual: '수동 등록',
};

export const SOURCE_TYPE_VARIANTS = {
    override: 'danger',
    note: 'info',
    feedback_edit: 'warning',
    manual: 'secondary',
};

// 서버 원시 feedback.status — 백엔드 _derive_feedback_status 가 proposal/target 진실에서
// 파생해 내려주는 5개. 인박스 배지는 이 원시값을 그대로 쓰지 않고 feedbackProgressKey 로
// 한 번 더 세분화한다(아래).
export const FEEDBACK_STATUS_LABELS = {
    PENDING: '대기',
    IN_PROGRESS: '진행 중',
    READY: '검토 대기',
    APPLIED: '반영됨',
    DISMISSED: '기각',
};

export const FEEDBACK_STATUS_VARIANTS = {
    PENDING: 'primary',
    IN_PROGRESS: 'info',
    READY: 'warning',
    APPLIED: 'success',
    DISMISSED: 'light',
};

// 개선 제안(proposal) target 한 건의 상태 라벨/배지.
// target.status: PLANNED|GENERATING|READY|SAVED|ACTIVE|NO_CHANGE|FAILED|CANCELLED|SKIPPED
export const TARGET_STATUS_LABELS = {
    PLANNED: '생성 대기',
    GENERATING: '생성 중',
    READY: '검토 대기',
    SAVED: '저장됨',
    ACTIVE: '활성화됨',
    NO_CHANGE: '반영 불필요',
    FAILED: '생성 실패',
    CANCELLED: '취소됨',
    SKIPPED: '초안 스킵',
};

export const TARGET_STATUS_VARIANTS = {
    PLANNED: 'secondary',
    GENERATING: 'info',
    READY: 'warning',
    SAVED: 'info',
    ACTIVE: 'success',
    NO_CHANGE: 'secondary',
    FAILED: 'danger',
    CANCELLED: 'light',
    SKIPPED: 'light',
};

// 백엔드가 proposal 완료 판정에 쓰는 "승인 종결" target 상태 집합.
// (_ACCEPTED_TARGET_TERMINALS 와 동일) — 인박스 보조문구의 "종결 N/전체" 계산에 쓴다.
// ⛔ CANCELLED/FAILED 는 종결로 세지 않는다(백엔드도 완료를 막는다).
export const ACCEPTED_TARGET_TERMINALS = ['SAVED', 'ACTIVE', 'NO_CHANGE', 'SKIPPED'];

// 인박스 진행 상태 — 요구된 8개 표시 라벨. 서버 feedback.status 를 1차로 쓰고
// 제품 문구상 꼭 필요한 세 가지(일부 저장됨/활성화 대기/반영 불필요)만 targets 로 세분화한다.
export const FEEDBACK_PROGRESS_LABELS = {
    PENDING: '반영 대기',
    IN_PROGRESS: '개선안 생성 중',
    READY: '검토 대기',
    PARTIAL_SAVED: '일부 저장됨',
    ACTIVATION_PENDING: '활성화 대기',
    APPLIED: '반영 완료',
    NO_CHANGE: '반영 불필요',
    DISMISSED: '기각',
};

export const FEEDBACK_PROGRESS_VARIANTS = {
    PENDING: 'primary',
    IN_PROGRESS: 'info',
    READY: 'warning',
    PARTIAL_SAVED: 'info',
    ACTIVATION_PENDING: 'warning',
    APPLIED: 'success',
    NO_CHANGE: 'secondary',
    DISMISSED: 'light',
};

// 서버 feedback.status + proposal targets 로 인박스 표시 key 를 계산한다.
// 백엔드 파생 로직을 통째로 복제하지 않고, 서버가 못 나누는 세 갈래만 보정한다.
export function feedbackProgressKey(item) {
    const status = item && item.status;
    if (status === 'DISMISSED') return 'DISMISSED';
    if (status === 'PENDING') return 'PENDING';
    if (status === 'IN_PROGRESS') return 'IN_PROGRESS';

    const proposal = item && item.proposal;
    const targets = (proposal && proposal.targets) || [];
    const statuses = targets.map((t) => t.status);

    if (status === 'READY') {
        // 이미 저장/활성화된 target 이 하나라도 있으면 "일부 저장됨".
        const hasPersisted = statuses.some((s) => s === 'SAVED' || s === 'ACTIVE');
        return hasPersisted ? 'PARTIAL_SAVED' : 'READY';
    }
    if (status === 'APPLIED') {
        const allNoOp = targets.length > 0
            && statuses.every((s) => s === 'NO_CHANGE' || s === 'SKIPPED');
        if ((proposal && proposal.status === 'NO_CHANGE') || allNoOp) return 'NO_CHANGE';
        // 아직 비활성(SAVED) target 이 남아 있으면 "활성화 대기".
        if (statuses.some((s) => s === 'SAVED')) return 'ACTIVATION_PENDING';
        return 'APPLIED';
    }
    return 'PENDING';
}

export function feedbackProgressLabel(item) {
    return FEEDBACK_PROGRESS_LABELS[feedbackProgressKey(item)] || '반영 대기';
}

export function feedbackProgressVariant(item) {
    return FEEDBACK_PROGRESS_VARIANTS[feedbackProgressKey(item)] || 'secondary';
}

export function targetStatusLabel(status) {
    return TARGET_STATUS_LABELS[status] || status || '-';
}

export function targetStatusVariant(status) {
    return TARGET_STATUS_VARIANTS[status] || 'secondary';
}

// apply-preview 가 피드백별로 내려주는 3-way 처분.
// 라벨은 평가자가 "내 피드백이 어떻게 됐나"를 바로 알 수 있는 말로 쓴다 —
// skip/add/absorb 는 내부 용어이고, '스킵'·'흡수'는 그대로 옮겨봤자 뜻이 안 통한다.
export const DISPOSITION_LABELS = {
    skip: '이미 반영돼 있음',
    add: '새 기준으로 추가',
    absorb: '기존 기준 보강',
};

export const DISPOSITION_VARIANTS = {
    skip: 'secondary',
    add: 'success',
    absorb: 'info',
};

// 골든 게이트 mismatch 의 expected/got — 보완검토 화면과 동일 용어.
export const JUDGEMENT_LABELS = {
    NO_SUPPLEMENT: '보완 불필요',
    SUPPLEMENT_REQUIRED: '보완 필요',
    REVIEW_REQUIRED: '평가자 확인',
    PROCESSING_FAILED: '처리 실패',
};

export function assetTypeLabel(type) {
    return ASSET_TYPE_LABELS[type] || type || '-';
}

export function sourceTypeLabel(type) {
    return SOURCE_TYPE_LABELS[type] || type || '-';
}

export function judgementLabel(status) {
    return JUDGEMENT_LABELS[status] || status || '-';
}

export function formatDateTime(iso) {
    if (!iso) return '-';
    return String(iso).replace('T', ' ').slice(0, 16);
}

// 일치율(0~1) → 백분율 문자열. 판정 대상이 없으면 백엔드가 null 을 준다.
export function formatRate(rate) {
    if (rate === null || rate === undefined) return '-';
    return `${(Number(rate) * 100).toFixed(1)}%`;
}

// ── URL query (?status=·?current_page= 등) ──────────────────────────────
export function firstQueryValue(value) {
    return Array.isArray(value) ? value[0] : value;
}

// 양의 정수만 받는다 — 빈 값·0·음수·소수·문자 섞인 값은 null.
export function queryPositiveInt(value) {
    const raw = String(firstQueryValue(value) ?? '');
    return /^[1-9]\d{0,9}$/.test(raw) ? Number(raw) : null;
}

// 키 순서·숫자/문자열·undefined 키 차이를 무시하고 같은 query 인지 본다(같으면 이동하지 않는다).
export function sameQuery(a, b) {
    const canon = (q) => JSON.stringify(Object.keys(q || {})
        .filter((k) => q[k] !== undefined && q[k] !== null)
        .sort()
        .map((k) => [k, String(q[k])]));
    return canon(a) === canon(b);
}
